<template>
    <div id="contents" class="contents spirit-of-japan">
        <section class="main">
            <div class="texts">
                <div class="inner">
                    <p class="sub">A Journey to the True Spirit of Japan</p>
                    <h2>The Journey of Japan's<br>
                      Original Landscapes Shaped by Water</h2>
                    <p class="lead">
                      Encircled by the sea and blessed with an abundance of water, the island nation of Japan has long been home to people who built their lives along the water's edge, expressing gratitude for the rich bounty of the ocean.<br>
                      <br>
                      A lake that enshrines faith, waterscapes that inspire art, a food culture born of aquatic bounty, and a strait that transformed into an international gateway.<br>
                      <br>
                      In these places, the colors of the four seasons have been passed down along with the aspirations and culture of the people.<br>
                      Explore Japan Heritage sites where one can truly gain a sense of the evolution of the nation’s original landscapes through lifestyles deeply intertwined with the sea and water.
                    </p>
                </div>
            </div>
            <div class="img"><img src="../../assets/spirit_of_japan4/main01.webp" alt="" class="_pc"><img src="../../assets/spirit_of_japan4/main01_sp.webp" alt="" class="_sp"></div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan4/img01.webp" alt=""></div>
                <div class="txt">
                    <h3>Onomichi: A Miniature Garden City Bursting with Creative Inspiration</h3>
                    <p>
                      Onomichi in Hiroshima Prefecture flourished as a port town thanks to the blessings of the Onomichi Channel, which runs through the heart of the city and can be regarded as the “river of the sea.” Within that space, numerous temples, shrines, gardens, and residences were built, and together with the intricate alleys and slopes connecting them, a miniature garden city formed that still retains all of the charms from the medieval to modern eras. The mazelike alleys and unexpected vistas that suddenly unfold beyond the slopes have continued to offer inspiration to writers, artists, and filmmakers, who have used them as settings for their works. By taking a stroll around Onomichi, you are bound to encounter scenery as stunning as a painting itself.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '5bca15cb-078a-4992-a6de-81631516a607', page: 'about', url: 'onomichi-port-hiroshima-jodoji-saikokuji-ushitora-shimanami'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan4/img02.webp" alt=""></div>
                <div class="txt">
                    <h3>A Unique Way of Life Firmly Rooted in Japan’s Largest Lake</h3>
                    <p>
                      Lake Biwa in Shiga Prefecture is Japan's largest freshwater lake. This massive body of water has enriched people's lives since ancient times and served as a cradle for culture and faith. Lake Biwa is home to over 1,000 diverse species of flora and fauna, including approximately 50 indigenous species. Down through time, lakeside hamlets have developed their own distinct food cultures, such as funazushi, a fermented fish and rice dish. The lakeside districts and waterscapes that sustain a true diversity of lifeforms have long been the inspiration for art and gardens, however in recent years these places are also capturing the attention of many modernists as cultural landscapes symbolizing harmony between water and people.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '84f5ab55-5d1d-43f2-a14c-2dc8d758197b', page: 'about', url:'lake-biwa-shiga-kabata-hieizan-enryakuji-shirahige-shrine'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan4/img03.webp" alt=""></div>
                <div class="txt">
                    <h3>Japan’s Most-Beloved Port Town - Tomonoura</h3>
                    <p>
                      Tucked away on the coast of eastern Hiroshima Prefecture, amidst the archipelago beauty of the Seto Inland Sea, is the seaside town of Tomonoura - Japan's only port settlement where harbor facilities dating back to the Edo period (1603–1868) have been collectively preserved. At dusk, Joyato Stone Lighthouse, recognized as the symbol of Tomonoura for 160 years, lights up the harbor, loyally watching over ships in the nearby waters and the people at the harbor. This scenic town has provided the setting for a Hollywood blockbuster as well as a Japanese TV drama series.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '17aa9650-6f70-4ba9-84bf-41fe6c9f5434', page: 'about', url: 'old-port-tomonoura-hiroshima-setonaikai-fukuyama'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan4/img04.webp" alt=""></div>
                <div class="txt">
                    <h3>Mackerel Road – Connecting the Sea and Kyoto</h3>
                    <p>
                      Throughout Japan’s Heian period (794-1185), the region of Wakasa (present-day Fukui Prefecture), situated adjacent to the Sea of Japan, played an important role as a <i>miketsukuni</i> – in other words, a province that supplied salt, seafood, and other foods to the imperial court in Kyoto, Japan’s ancient capital. It was also the foremost hub where the maritime routes connecting to the continent and the land routes leading to Kyoto converged. Throughout the history of travel dating back to ancient times, ports, castle towns, and post towns flourished along the routes. Festivals, performing arts, and Buddhist culture brought by travelers spread widely from these routes to farming and fishing villages, where they went on to evolve in unique ways. Along this series of routes known in recent years as the “Mackerel Highway,” the townscapes that speak of its former bustle coexist with lush natural surroundings and diverse cultures—such as traditional foods and festivals—that continue to thrive to this day.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '0ab68603-d5a7-48c5-9870-c177a78ff098', page: 'about', url: 'wakasa-sea-kyoto-imperial-food-mackerel-road-fukui'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan4/img05.webp" alt=""></div>
                <div class="txt">
                    <h3>Straits of Nostalgia - Where Retro Streetscapes Remain</h3>
                    <p>
                      The Kanmon Straits is the stretch of water separating Japan’s main island of Honshu and the island of Kyushu. This waterway is the route via which many ships from the West arrived in Japan during the late Edo period (mid 19th century). The ports of Shimonoseki and Moji on either side of the strait acted as international gateways featuring Western-style lighthouses. In the strategic port of Shimonoseki, Great Britain constructed a British Consulate made of red brick. Later, financial and trading companies built modern buildings, and the city still retains its retro structures from that period in its history. <br>
                      A specialty of this region is fugu (pufferfish) cuisine, considered auspicious because its name sounds similar to the word “fuku” (meaning “good fortune”). Visitors can indulge in fugu prepared in various different ways, such as sashimi, hot pot, deep-fried, milt, or fin sake.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '2ccebe8a-4206-40e5-99c4-f12eff965579', page: 'about', url: 'kanmon-straits-modernization-fukuoka-yamaguchi'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
    </section>
    <section class="section03">
            <div class="inner">
                <h2>Other Articles</h2>
                <ul>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan1'}">
                            <div class="img"><img src="../../assets/spirit_of_japan/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Discover Craftsmanship Unbowed by Time</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan2'}">
                            <div class="img"><img src="../../assets/spirit_of_japan2/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Sacred Sites where Nature and Faith Blend into One</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan3'}">
                            <div class="img"><img src="../../assets/spirit_of_japan3/other.png" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Unlocking the Secrets of the Shogun, Samurai and Ninja</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan5'}">
                            <div class="img"><img src="../../assets/spirit_of_japan5/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Encounter Festivals Embodying the Japanese Spirit</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan6'}">
                            <div class="img"><img src="../../assets/spirit_of_japan6/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Ancient Paths
                                    Where Japanese Culture Dwells</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan7'}">
                            <div class="img"><img src="../../assets/spirit_of_japan7/main01.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">“O-sake” — the drink that nurtured Japanese history</div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import ApiRouter from "../../components/Systems/ApiRouter";

export default {
    name: "PlanYourVisit",
    mixins: [ApiRouter],
    data: function () {
        return {
            articles: [],
            activeTabMenu: 'half'
        }
    },
    created: function () {
        this.loadData();
        this.getQuery();
        document.title = 'Spirit Of Japan | Japan Heritage Official Site'
    },
    methods: {
        loadData: function () {
            window.axios.get(this.routes.articles)
                .then((response) => {
                    this.articles = response.data.data;
                });
        },
        getQuery: function () {
            const tab = this.$route.query.tab;

            switch (tab) {
                case 'day1':
                    this.activeTabMenu = 'day1';
                    break;
                case 'day2':
                    this.activeTabMenu = 'day2';
                    break;
                case 'day3':
                    this.activeTabMenu = 'day3';
                    break;
                default:
                    this.activeTabMenu = 'half';
                    break;
            }
        },
        articleCardHasClicked: function (id, hash, breadcrumb) {
            this.$router.push({name: 'Popular', params: {id: id, page: 'travelInfo', breadcrumb: breadcrumb}, hash: hash});
        }
    },
    watch: {
        $route() {
            this.getQuery();
        }
    }
}
</script>

<style src="./SpiritOfJapan4.scss" lang="scss" scoped />
